import {
	IconBriefcase,
	IconCircleCheckFilled,
	IconCircleDashed,
	IconList,
	IconPlus,
	IconTerminal2,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	Button,
	Card,
	Col,
	Collapse,
	Flex,
	Row,
	Space,
	Typography,
	theme,
} from "antd";
import { format, formatDistanceToNow, subDays } from "date-fns";
import { lazy, Suspense, useEffect, useMemo, useReducer } from "react";
import { useAgents } from "@/common/artemis-operations/agents";
import { useGitHubAvailable } from "@/common/artemis-operations/apps";
import { useEnvironments } from "@/common/artemis-operations/environments";
import { useIsWorkspaceAdmin } from "@/common/artemis-operations/members";
import {
	useIsOrgAdmin,
	useOrganizationMetrics,
	useOrgDomains,
} from "@/common/artemis-operations/organization";
import { useProjects } from "@/common/artemis-operations/projects";
import { useTasks } from "@/common/artemis-operations/tasks";
import {
	useWorkspaceInfo,
	useWorkspaceMetrics,
} from "@/common/artemis-operations/workspaces";
import type { ArtemisTask, Project } from "@/common/model";
import { toLabel } from "@/common/utils";
import { BaseContainer, CustomIcon } from "@/components/base";
import { CustomList } from "@/components/custom-list";
import { ProjectStatusBadge } from "@/components/projects/project-badges";
import { TaskStatusBadge } from "@/components/tasks/task-badges";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

// @ant-design/plots is a large charting lib used only here — load it on demand
// instead of bundling it into every route.
const TasksByDayChart = lazy(() =>
	import("@/components/workspaces/tasks-by-day-chart").then((m) => ({
		default: m.TasksByDayChart,
	})),
);

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/",
)({
	component: WorkspaceHome,
});

const ago = (v: unknown) =>
	v ? formatDistanceToNow(new Date(v as string), { addSuffix: true }) : null;

/** Tiny inline sparkline. ponytail: mock trend data until the backend exposes history. */
function Sparkline({ data, color }: { data: number[]; color: string }) {
	const w = 72;
	const h = 22;
	const max = Math.max(...data);
	const min = Math.min(...data);
	const span = max - min || 1;
	const pts = data
		.map((v, i) => {
			const x = (i / (data.length - 1)) * w;
			const y = h - ((v - min) / span) * (h - 2) - 1;
			return `${x.toFixed(1)},${y.toFixed(1)}`;
		})
		.join(" ");
	return (
		<svg
			width={w}
			height={h}
			role="img"
			aria-label="Weekly trend"
			style={{ display: "block" }}
		>
			<title>Weekly trend</title>
			<polyline
				points={pts}
				fill="none"
				stroke={color}
				strokeWidth={1.5}
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

/** Dense KPI tile. Optionally shows a sparkline and a week-over-week delta. */
function KpiTile({
	label,
	value,
	spark,
	delta,
	tone,
	onClick,
}: {
	label: string;
	value: number;
	spark?: number[];
	delta?: number;
	/** Value color — for counts that need attention when non-zero. */
	tone?: string;
	onClick: () => void;
}) {
	const { token } = theme.useToken();
	const up = (delta ?? 0) >= 0;
	return (
		<Card
			hoverable
			size="small"
			onClick={onClick}
			style={{ height: "100%" }}
			styles={{ body: { padding: 12 } }}
		>
			<Flex justify="space-between" align="flex-start" gap={8}>
				<div>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						{label}
					</Typography.Text>
					<div
						style={{
							fontSize: 24,
							fontWeight: 600,
							lineHeight: 1.25,
							color: tone,
						}}
					>
						{value}
					</div>
					{delta !== undefined && (
						<Typography.Text
							style={{
								fontSize: 12,
								color: up ? token.colorSuccess : token.colorError,
							}}
						>
							{up ? "▲" : "▼"} {Math.abs(delta)} vs last week
						</Typography.Text>
					)}
				</div>
				{spark && <Sparkline data={spark} color={token.colorPrimary} />}
			</Flex>
		</Card>
	);
}

function LegendRow({
	color,
	label,
	value,
}: {
	color: string;
	label: string;
	value: number;
}) {
	return (
		<Flex align="center" gap={8}>
			<span
				style={{
					width: 8,
					height: 8,
					borderRadius: "50%",
					background: color,
					display: "inline-block",
				}}
			/>
			<Typography.Text type="secondary" style={{ fontSize: 13 }}>
				{label}
			</Typography.Text>
			<Typography.Text strong style={{ fontSize: 13 }}>
				{value}
			</Typography.Text>
		</Flex>
	);
}

/** Segmented completion ring (completed / in-progress / failed) with the total in the hole. */
function ProgressRing({
	completed,
	inProgress,
	failed,
}: {
	completed: number;
	inProgress: number;
	failed: number;
}) {
	const { token } = theme.useToken();
	const total = completed + inProgress + failed;
	const segs = [
		{ v: completed, color: token.colorSuccess },
		{ v: inProgress, color: token.colorPrimary },
		{ v: failed, color: token.colorError },
	].filter((s) => s.v > 0);

	let acc = 0;
	const stops = segs
		.map((s) => {
			const start = (acc / total) * 360;
			acc += s.v;
			const end = (acc / total) * 360;
			return `${s.color} ${start}deg ${end}deg`;
		})
		.join(", ");
	const ring =
		total > 0 ? `conic-gradient(${stops})` : token.colorFillSecondary;

	return (
		<Flex align="center" gap={24} wrap>
			<div
				style={{
					position: "relative",
					width: 128,
					height: 128,
					borderRadius: "50%",
					background: ring,
					flexShrink: 0,
				}}
			>
				<div
					style={{
						position: "absolute",
						inset: 14,
						borderRadius: "50%",
						background: token.colorBgContainer,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>
						{total}
					</div>
					<Typography.Text type="secondary" style={{ fontSize: 12 }}>
						total tasks
					</Typography.Text>
				</div>
			</div>
			<Space orientation="vertical" size={8}>
				<LegendRow
					color={token.colorSuccess}
					label="Completed"
					value={completed}
				/>
				<LegendRow
					color={token.colorPrimary}
					label="In progress"
					value={inProgress}
				/>
				<LegendRow color={token.colorError} label="Failed" value={failed} />
			</Space>
		</Flex>
	);
}

function WorkspaceHome() {
	const navigate = useNavigate();

	const { orgSlug, workspaceId } = Route.useParams();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { token } = theme.useToken();
	const {
		openTaskCreateWizard,
		openProjectCreateWizard,
		openEnvironmentCreateWizard,
		openAgentCreateWizard,
		openCliSetupWizard,
	} = useWizards();

	const { data: workspace } = useWorkspaceInfo(workspaceId);
	const { data: projects } = useProjects(workspaceId);
	const { data: tasks } = useTasks(workspaceId);
	const { data: environments } = useEnvironments(workspaceId);
	const { data: agents } = useAgents(workspaceId);
	const { data: metrics } = useWorkspaceMetrics(workspaceId);
	const { data: orgMetrics } = useOrganizationMetrics();
	const { data: domains } = useOrgDomains();
	const { available: gitHubAvailable } = useGitHubAvailable(workspaceId);
	const { isAdmin: isOrgAdmin } = useIsOrgAdmin();
	const isAdmin = useIsWorkspaceAdmin(workspaceId) || isOrgAdmin;
	const agentQuotaReached =
		orgMetrics?.maxAgents != null &&
		orgMetrics.agentCount >= orgMetrics.maxAgents;
	const envQuotaReached =
		orgMetrics?.maxEnvironments != null &&
		orgMetrics.environmentCount >= orgMetrics.maxEnvironments;

	// Onboarding checklist — see notes below; read flags from localStorage each render.
	const [, bumpOnboarding] = useReducer((n: number) => n + 1, 0);
	const onboardingKey = `artemis-onboarding-dismissed-${workspaceId}`;
	const onboardingDismissed = !!localStorage.getItem(onboardingKey);
	const checkedKey = `artemis-onboarding-checked-${workspaceId}`;
	const checkedSteps: string[] = JSON.parse(
		localStorage.getItem(checkedKey) || "[]",
	);
	const isChecked = (key: string) => checkedSteps.includes(key);
	const toggleChecked = (key: string) => {
		const next = isChecked(key)
			? checkedSteps.filter((k) => k !== key)
			: [...checkedSteps, key];
		localStorage.setItem(checkedKey, JSON.stringify(next));
		bumpOnboarding();
	};

	type OnboardingStep = {
		key: string;
		label: string;
		done: boolean;
		action: () => void;
		optional?: boolean;
		toggleable?: boolean;
		actionLabel?: string;
		disabled?: boolean;
	};
	const onboardingSteps: OnboardingStep[] = [
		{
			key: "environment",
			label: "Add at least one environment",
			done: (environments?.length ?? 0) >= 1,
			action: openEnvironmentCreateWizard,
			disabled: envQuotaReached,
		},
		{
			key: "project",
			label: "Create a project",
			done: (projects?.length ?? 0) >= 1,
			action: openProjectCreateWizard,
		},
		{
			key: "cli",
			label: "Set up the CLI for external agent access",
			optional: true,
			toggleable: true,
			done: isChecked("cli"),
			action: () => openCliSetupWizard(),
			actionLabel: "Set up",
		},
		{
			key: "agent",
			label: "Add an agent",
			optional: true,
			toggleable: (agents?.length ?? 0) < 1,
			done: (agents?.length ?? 0) >= 1 || isChecked("agent"),
			action: openAgentCreateWizard,
			disabled: agentQuotaReached,
		},
		{
			key: "task",
			label: "Create your first task",
			done: (tasks?.length ?? 0) >= 1,
			action: openTaskCreateWizard,
		},
		// Admin-only: verifying a domain and enabling integrations both require
		// admin access on the pages they link to, so there's nothing for a
		// non-admin to do with these steps.
		...(isAdmin
			? [
					{
						key: "domain",
						label: "Verify your organization's domain",
						optional: true,
						done: domains?.some((d) => !!d.verifiedAt) ?? false,
						action: () =>
							navigate({ to: "/$orgSlug/admin", params: { orgSlug } }),
						actionLabel: "Verify",
					},
					{
						key: "integrations",
						label: "Enable an integration (e.g. GitHub)",
						optional: true,
						done: gitHubAvailable,
						action: () =>
							navigate({
								to: "/$orgSlug/workspaces/$workspaceId/settings",
								params: { orgSlug, workspaceId },
							}),
						actionLabel: "Enable",
					},
				]
			: []),
	];
	const completedSteps = onboardingSteps.filter((s) => s.done).length;
	const showOnboarding =
		!onboardingDismissed && completedSteps < onboardingSteps.length;
	const dismissOnboarding = () => {
		localStorage.setItem(onboardingKey, "1");
		bumpOnboarding();
	};

	useEffect(() => {
		setTitle(toLabel(workspace?.name ?? "Workspace"));
		setActions(
			isAdmin
				? [
						{
							type: "button",
							id: "cli-setup",
							tooltip: "Set up the stratonext CLI",
							icon: <CustomIcon icon={IconTerminal2} />,
							onClick: () => openCliSetupWizard(),
						},
						{
							type: "submenu",
							id: "create",
							label: "Create",
							icon: <CustomIcon icon={IconPlus} />,
							submenu: [
								{
									type: "button",
									id: "create-project",
									label: "Project",
									onClick: () => openProjectCreateWizard(),
								},
								{
									type: "button",
									id: "create-task",
									label: "Task",
									onClick: () => openTaskCreateWizard(),
								},
								{
									type: "button",
									id: "create-environment",
									label: "Environment",
									disabled: envQuotaReached,
									onClick: () => openEnvironmentCreateWizard(),
								},
								{
									type: "button",
									id: "create-agent",
									label: "Agent",
									disabled: agentQuotaReached,
									onClick: () => openAgentCreateWizard(),
								},
							],
						},
					]
				: [],
		);
	}, [
		setTitle,
		workspace,
		setActions,
		isAdmin,
		openTaskCreateWizard,
		openProjectCreateWizard,
		openEnvironmentCreateWizard,
		openAgentCreateWizard,
		openCliSetupWizard,
		envQuotaReached,
		agentQuotaReached,
	]);

	const base = `/${orgSlug}/workspaces/${workspaceId}`;
	// `search` pre-selects the target list's filter, e.g. { status: "FAILED" }.
	const go = (path: string, search?: Record<string, string>) =>
		navigate({ to: path as "/", search: search as never });

	const tasksByDay = useMemo(() => {
		const counts: Record<string, number> = {};
		for (let i = 29; i >= 0; i--)
			counts[format(subDays(new Date(), i), "MMM d")] = 0;
		for (const t of tasks ?? []) {
			const key = format(new Date(t.createdAt as string), "MMM d");
			if (key in counts) counts[key]++;
		}
		return Object.entries(counts).map(([date, count]) => ({ date, count }));
	}, [tasks]);

	const activeTasks = metrics?.activeTasks ?? 0;
	const completedTasks = metrics?.completedTasks ?? 0;
	const failedTasks = metrics?.failedTasks ?? 0;
	const pendingApprovals = metrics?.pendingApprovals ?? 0;

	// Real "new tasks" trend, reused from the 30-day created-per-day series below.
	const newTasksSpark = tasksByDay.slice(-7).map((d) => d.count);
	const newTasksLast7 = newTasksSpark.reduce((a, b) => a + b, 0);
	const newTasksPrev7 = tasksByDay
		.slice(-14, -7)
		.reduce((a, d) => a + d.count, 0);
	const newTasksDelta = newTasksLast7 - newTasksPrev7;

	const activeProjects = (projects ?? []).filter(
		(p) => p?.status === "ACTIVE",
	).length;
	const readyAgents = (agents ?? []).filter(
		(a) => a?.health === "READY",
	).length;
	const failedAgents = (agents ?? []).filter(
		(a) => a?.health === "FAILED",
	).length;
	const prodEnvironments = (environments ?? []).filter(
		(e) => e?.classification === "PRODUCTION",
	).length;

	const demoted = [
		{
			label: "Projects",
			value: projects?.length ?? 0,
			path: `${base}/projects`,
			segments: [
				{ value: activeProjects, label: "active", color: token.colorSuccess },
				{
					value: (projects?.length ?? 0) - activeProjects,
					label: "archived",
					color: token.colorTextTertiary,
				},
			],
		},
		{
			label: "Agents",
			value: agents?.length ?? 0,
			path: `${base}/agents`,
			segments: [
				{ value: readyAgents, label: "ready", color: token.colorSuccess },
				{ value: failedAgents, label: "failed", color: token.colorError },
			],
		},
		{
			label: "Environments",
			value: environments?.length ?? 0,
			path: `${base}/environments`,
			segments: [
				{
					value: prodEnvironments,
					label: "production",
					color: token.colorWarning,
				},
				{
					value: (environments?.length ?? 0) - prodEnvironments,
					label: "non-prod",
					color: token.colorTextTertiary,
				},
			],
		},
	];

	return (
		<Flex vertical gap={16} style={{ padding: 24, maxWidth: 1600 }}>
			{/* Primary KPIs — dense, with trend context */}
			<Row gutter={[16, 16]}>
				{/* Needs-attention counts are KPIs too: tinted when non-zero, routing to the source */}
				<Col xs={24} md={8}>
					<KpiTile
						label="New tasks (7d)"
						value={newTasksLast7}
						spark={newTasksSpark}
						delta={newTasksDelta}
						onClick={() => go(`${base}/tasks`)}
					/>
				</Col>
				<Col xs={12} md={4}>
					<KpiTile
						label="Active tasks"
						value={activeTasks}
						onClick={() => go(`${base}/tasks`)}
					/>
				</Col>
				<Col xs={12} md={4}>
					<KpiTile
						label="Completed tasks"
						value={completedTasks}
						onClick={() => go(`${base}/tasks`, { status: "COMPLETED" })}
					/>
				</Col>
				<Col xs={12} md={4}>
					<KpiTile
						label="Failed tasks"
						value={failedTasks}
						tone={failedTasks > 0 ? token.colorError : undefined}
						onClick={() => go(`${base}/tasks`, { status: "FAILED" })}
					/>
				</Col>
				<Col xs={12} md={4}>
					<KpiTile
						label="Pending approvals"
						value={pendingApprovals}
						tone={pendingApprovals > 0 ? token.colorWarning : undefined}
						onClick={() => go(`${base}/approvals`, { status: "PENDING" })}
					/>
				</Col>
				{/* Demoted vanity counts — smaller cards, not equal-weight with the KPIs */}
				{demoted.map((d) => (
					<Col xs={8} md={8} key={d.label}>
						<Card
							hoverable
							size="small"
							onClick={() => go(d.path)}
							style={{ height: "100%" }}
							styles={{ body: { padding: 12 } }}
						>
							<Typography.Text type="secondary" style={{ fontSize: 12 }}>
								{d.label}
							</Typography.Text>
							<div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.25 }}>
								{d.value}
							</div>
							<Flex gap={10} wrap style={{ marginTop: 2 }}>
								{d.segments.map((s) => (
									<Flex key={s.label} align="center" gap={4}>
										<span
											style={{
												width: 6,
												height: 6,
												borderRadius: "50%",
												background: s.color,
												display: "inline-block",
											}}
										/>
										<Typography.Text style={{ fontSize: 12, color: s.color }}>
											{s.value} {s.label}
										</Typography.Text>
									</Flex>
								))}
							</Flex>
						</Card>
					</Col>
				))}
			</Row>

			{/* Getting-started checklist — auto-computed, dismissable */}
			{showOnboarding && (
				<Collapse
					defaultActiveKey={["getting-started"]}
					items={[
						{
							key: "getting-started",
							label: (
								<Flex align="center" gap={8}>
									<Typography.Text strong>Getting started</Typography.Text>
									<Typography.Text type="secondary">
										{completedSteps}/{onboardingSteps.length}
									</Typography.Text>
								</Flex>
							),
							extra: (
								<Typography.Link
									type="secondary"
									onClick={(e) => {
										e.stopPropagation();
										dismissOnboarding();
									}}
								>
									Dismiss
								</Typography.Link>
							),
							children: (
								<Space
									orientation="vertical"
									size="middle"
									style={{ width: "100%" }}
								>
									{onboardingSteps.map((step) => {
										const toggle = step.toggleable
											? () => toggleChecked(step.key)
											: undefined;
										return (
											<Flex
												key={step.key}
												align="center"
												justify="space-between"
											>
												<Flex align="center" gap={10}>
													{step.done ? (
														<IconCircleCheckFilled
															size={20}
															color={token.colorSuccess}
															style={toggle ? { cursor: "pointer" } : undefined}
															onClick={toggle}
														/>
													) : (
														<IconCircleDashed
															size={20}
															color={token.colorTextTertiary}
															style={toggle ? { cursor: "pointer" } : undefined}
															onClick={toggle}
														/>
													)}
													<Typography.Text
														type={step.done ? "secondary" : undefined}
														delete={step.done}
													>
														{step.label}
														{step.optional && (
															<Typography.Text type="secondary">
																{" "}
																(optional)
															</Typography.Text>
														)}
													</Typography.Text>
												</Flex>
												{!step.done && (
													<Button
														size="small"
														type="link"
														disabled={step.disabled}
														onClick={step.action}
													>
														{step.actionLabel ?? "Add"}
													</Button>
												)}
											</Flex>
										);
									})}
								</Space>
							),
						},
					]}
				/>
			)}

			{/* Charts — equal height; the ring card stretches to match the bar chart */}
			<Row gutter={[16, 16]} align="stretch">
				<Col xs={24} md={14}>
					<Card
						title="Tasks created (last 30 days)"
						size="small"
						style={{ height: "100%" }}
					>
						<Suspense fallback={null}>
							<TasksByDayChart data={tasksByDay} />
						</Suspense>
					</Card>
				</Col>
				<Col xs={24} md={10}>
					<Card
						title="Task progress"
						size="small"
						style={{ height: "100%" }}
						styles={{
							body: {
								height: "calc(100% - 38px)",
								display: "flex",
								alignItems: "center",
							},
						}}
					>
						<ProgressRing
							completed={completedTasks}
							inProgress={activeTasks}
							failed={failedTasks}
						/>
					</Card>
				</Col>
			</Row>

			{/* Top projects */}
			<BaseContainer
				size="small"
				title="Projects"
				actions={
					<Link
						to="/$orgSlug/workspaces/$workspaceId/projects"
						params={{ orgSlug, workspaceId }}
					>
						See all
					</Link>
				}
			>
				<CustomList<NonNullable<Project>>
					data={(projects ?? []).filter((p) => !!p).slice(0, 5)}
					emptyText="Projects"
					onAdd={() => openProjectCreateWizard()}
					onItemClick={(p) =>
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/projects/$projectId",
							params: { orgSlug, workspaceId, projectId: p.id },
						})
					}
					renderLeading={() => <CustomIcon icon={IconBriefcase} />}
					renderTrailing={(p) =>
						p.status && <ProjectStatusBadge status={p.status} />
					}
					renderItem={(p) => (
						<Flex vertical style={{ minWidth: 0 }}>
							<Typography.Text ellipsis={{ tooltip: p.name }}>
								{p.name}
							</Typography.Text>
							<Typography.Text
								type="secondary"
								style={{ fontSize: 12 }}
								ellipsis={{ tooltip: true }}
							>
								{ago(p.createdAt) ? `Created ${ago(p.createdAt)}` : "-"}
							</Typography.Text>
						</Flex>
					)}
				/>
			</BaseContainer>

			{/* Recent tasks */}
			<BaseContainer
				size="small"
				title="Recent tasks"
				actions={
					<Link
						to="/$orgSlug/workspaces/$workspaceId/tasks"
						params={{ orgSlug, workspaceId }}
					>
						See all
					</Link>
				}
			>
				<CustomList<ArtemisTask>
					data={tasks?.slice(0, 15) ?? []}
					emptyText="Tasks"
					onAdd={() => openTaskCreateWizard()}
					onItemClick={(t) =>
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/tasks/$taskId",
							params: { orgSlug, workspaceId, taskId: t.id },
						})
					}
					renderLeading={() => <CustomIcon icon={IconList} />}
					renderTrailing={(t) => <TaskStatusBadge status={t.status} />}
					renderItem={(t) => (
						<Flex vertical style={{ minWidth: 0 }}>
							<Typography.Text ellipsis={{ tooltip: t.name }}>
								{t.name}
							</Typography.Text>
							<Typography.Text
								type="secondary"
								style={{ fontSize: 12 }}
								ellipsis={{ tooltip: true }}
							>
								{ago(t.createdAt) ? `Created ${ago(t.createdAt)}` : "-"}
							</Typography.Text>
						</Flex>
					)}
				/>
			</BaseContainer>
		</Flex>
	);
}
